import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import SearchField from "@khanacademy/wonder-blocks-search-field";
import {border, color} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    LabelLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {getLanguages, type LanguageData} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import getCountries, {safeGetCountryByCode} from "./countries.ts";
import {WorldMap} from "./world-map.tsx";

const COUNTRY_COLOR_BY_MINUTES = [
    "#f7e64d",
    "#a8d460",
    "#6fb579",
    "#4e9588",
    "#71837f",
];
const COUNTRY_HIGHLIGHT_COLOR = "#FFB100";

const strings = {
    getCountriesForLanguageLabel: (languageName: string) => (
        <Trans>Where do people use {languageName}?</Trans>
    ),
} as const;

type Props = Record<any, any>;

type ColorData = Record<string, string>;

type State = {
    colorData: ColorData;
    query: string;
    focusLanguage: string;
};

export default class LanguageMap extends React.Component<Props, State> {
    orginalColorData: ColorData;

    constructor(props: Props) {
        super(props);

        this.orginalColorData = this.getDefaultColorData();
        this.state = {
            colorData: {...this.orginalColorData},
            query: "",
            focusLanguage: "",
        };
    }

    getDefaultColorData(): ColorData {
        const colorData: ColorData = {};

        for (const [isoKey, countryData] of getCountries()) {
            const countryColorIndex = countryData.minutes;
            colorData[isoKey] =
                countryColorIndex > -1 &&
                countryColorIndex < COUNTRY_COLOR_BY_MINUTES.length
                    ? COUNTRY_COLOR_BY_MINUTES[countryColorIndex]
                    : color.white;
        }

        return colorData;
    }

    getSortedLanguageNames(): Array<[string, string]> {
        const languageNamesSorted: Array<[string, string]> = [];
        for (const [isoKey, languageData] of getLanguages()) {
            languageNamesSorted.push([isoKey, languageData.languageName]);
        }

        languageNamesSorted.sort((a, b) => {
            if (a[1] > b[1]) {
                return 1;
            }
            if (b[1] > a[1]) {
                return -1;
            }
            return 0;
        });
        return languageNamesSorted;
    }

    updateCountryColor(iso: string, color: string) {
        this.setState((prevState) => ({
            colorData: {
                ...prevState.colorData,
                [iso]: color,
            },
        }));
    }

    highlightCountry(iso: string): () => void {
        return () => {
            this.updateCountryColor(iso, COUNTRY_HIGHLIGHT_COLOR);
        };
    }

    unhighlightCountry(iso: string): () => void {
        return () => {
            this.updateCountryColor(iso, this.orginalColorData[iso]);
        };
    }

    handleSearchInputChange: (newValue: string) => unknown = (
        newValue: string,
    ) => {
        this.setState({
            query: newValue,
            focusLanguage: "",
            colorData: this.orginalColorData,
        });
    };

    renderExternalLinkButton(url: string, label: string): React.ReactNode {
        return (
            <Button
                style={styles.externalLinkButton}
                target="_blank"
                href={url}
                // NOTE(jvoll): purposely excluded "opens in a new tab" from
                // this label because if you hit the <enter> key it actually
                // opens in the same tab right now, it's only if you click it
                // that it opens in a new tab.
                aria-label={label}
            >
                {label}
            </Button>
        );
    }

    renderCountriesForLanguageList(language: LanguageData): React.ReactNode {
        // Sort the isoCodes for countries where a given language is spoken by
        // the translated country name so they are alphabetized by country name.
        const sortedCountries = language.countries.sort((a, b) =>
            safeGetCountryByCode(a).country < safeGetCountryByCode(b).country
                ? -1
                : 1,
        );

        const countries = getCountries();

        return (
            <View style={styles.countriesForLanguageList}>
                <LabelLarge style={styles.countryListLabel}>
                    {strings.getCountriesForLanguageLabel(
                        language.languageName,
                    )}
                </LabelLarge>

                {sortedCountries.map((iso) => {
                    return (
                        countries.has(iso) && (
                            <View
                                key={iso}
                                tabIndex={0}
                                onMouseEnter={this.highlightCountry(iso)}
                                onFocus={this.highlightCountry(iso)}
                                onMouseLeave={this.unhighlightCountry(iso)}
                                onBlur={this.unhighlightCountry(iso)}
                                style={styles.countryListItem}
                            >
                                <View
                                    style={styles.flagContainer}
                                    aria-hidden="true"
                                >
                                    <img
                                        src={staticUrl(
                                            "/images/about/language_map/flags/" +
                                                iso.toLowerCase() +
                                                ".png",
                                        )}
                                        alt=""
                                        className={css(styles.flag)}
                                    />
                                </View>
                                <LabelSmall>
                                    {safeGetCountryByCode(iso).country}
                                </LabelSmall>
                            </View>
                        )
                    );
                })}
            </View>
        );
    }

    renderLanguageModal(iso: string): React.ReactNode {
        const language = getLanguages().get(iso);
        if (!language) {
            return;
        }

        const languageName = language.languageName;
        const url = language.url ? language.url : null;
        const teamUrl = language.teamUrl ? language.teamUrl : null;

        const isEn = iso === "en";

        return (
            <View style={styles.scrollingContainer}>
                <HeadingLarge style={styles.languageTitle}>
                    {languageName}
                </HeadingLarge>
                {url &&
                    !isEn &&
                    this.renderExternalLinkButton(
                        url,
                        t`Go to the translated site`,
                    )}
                {teamUrl &&
                    this.renderExternalLinkButton(teamUrl, t`Meet the team`)}
                {!isEn &&
                    this.renderExternalLinkButton(
                        "https://www.khanacademy.org/r/applytotranslate",
                        t`Apply to Translate`,
                    )}
                {this.renderCountriesForLanguageList(language)}
            </View>
        );
    }

    langaugeClicked(iso: string) {
        const language = getLanguages().get(iso);
        if (!language) {
            return;
        }

        const countriesList: Array<string> = language.countries;
        const tempColorData: State["colorData"] = {};
        countriesList.forEach(
            (country) =>
                (tempColorData[country] = this.orginalColorData[country]),
        );

        this.setState({
            query: language.languageName,
            focusLanguage: iso,
            colorData: tempColorData,
        });
    }

    renderSearchModal(): React.ReactNode {
        return (
            <View style={styles.scrollingContainer}>
                {this.getSortedLanguageNames().map((row) => {
                    if (
                        row[1]
                            .toLowerCase()
                            .includes(this.state.query.toLowerCase())
                    ) {
                        return (
                            <Button
                                key={row[0]}
                                kind="secondary"
                                style={styles.languageListItem}
                                onClick={() => {
                                    this.langaugeClicked(row[0]);
                                }}
                            >
                                {row[1]}
                            </Button>
                        );
                    }
                })}
            </View>
        );
    }

    renderMapKey(): React.ReactNode {
        return (
            <View>
                <View style={styles.keyMapLabels}>
                    <LabelSmall>{t`<100K`}</LabelSmall>
                    <LabelSmall>{t`1M`}</LabelSmall>
                    <LabelSmall>{t`100M`}</LabelSmall>
                    <LabelSmall>{t`1B+`}</LabelSmall>
                </View>
                <View style={styles.keyMapGradient} />
                <LabelSmall style={styles.legendLabel}>
                    {t`Minutes on Khan Academy in 2018`}
                </LabelSmall>
            </View>
        );
    }

    render(): React.ReactNode {
        const {focusLanguage} = this.state;
        return (
            <View style={styles.mapContainer}>
                <View style={styles.languageSelectorContainer}>
                    <SearchField
                        value={this.state.query}
                        onChange={this.handleSearchInputChange}
                        placeholder={t`Search for a language`}
                        style={styles.searchField}
                    />
                    {focusLanguage
                        ? this.renderLanguageModal(focusLanguage)
                        : this.renderSearchModal()}
                </View>
                <View style={styles.mapAndKeyContainer}>
                    <View>
                        <WorldMap colorData={this.state.colorData} />
                    </View>
                    {this.renderMapKey()}
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    mapAndKeyContainer: {
        margin: 16,
        flexGrow: 2,
    },
    languageSelectorContainer: {
        backgroundColor: "#F7F8FA",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        minWidth: 275,
        height: 400,
        marginLeft: 16,
        marginTop: 16,
        marginBottom: 16,
        [mediaQueries.smOrSmaller]: {
            marginRight: 16,
            height: 200,
            minWidth: 150,
        },
    },
    searchField: {
        flexShrink: 0,
    },
    mapContainer: {
        backgroundColor: "#B5CEFB",
        flexDirection: "row",
        alignItems: "center",
        [mediaQueries.smOrSmaller]: {
            flexDirection: "column-reverse",
            alignItems: "stretch",
        },
    },
    scrollingContainer: {
        overflow: "auto",
    },
    languageListItem: {
        minHeight: 40,
        alignItems: "center",
        borderRadius: "none",
    },
    languageTitle: {
        paddingBottom: 8,
        marginTop: 16,
        textAlign: "center",
    },
    keyMapLabels: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
    keyMapGradient: {
        backgroundImage: `linear-gradient(to left,
            ${COUNTRY_COLOR_BY_MINUTES[0]},${COUNTRY_COLOR_BY_MINUTES[1]},
            ${COUNTRY_COLOR_BY_MINUTES[2]},${COUNTRY_COLOR_BY_MINUTES[3]},
            ${COUNTRY_COLOR_BY_MINUTES[4]})`,
        backgroundSize: "contain",
        height: 12,
        borderRadius: border.radius.radius_040,
    },
    externalLinkButton: {
        paddingBottom: 8,
        paddingTop: 8,
        marginTop: 8,
        marginLeft: 16,
        marginRight: 16,
    },
    countriesForLanguageList: {
        marginTop: 20,
        marginLeft: 16,
        marginRight: 16,
    },
    countryListItem: {
        backgroundColor: color.white,
        border: "1px solid rgba(33,36,44,0.16)",
        flexDirection: "row",
        minHeight: 50,
        alignItems: "center",
        marginBottom: 8,
        ":hover": {
            border: "1px solid #1865F2",
        },
        ":focus": {
            border: "1px solid #1865F2",
        },
    },
    flagContainer: {
        marginRight: 8,
        marginLeft: 8,
    },
    flag: {
        border: "1px solid rgba(0, 0, 0, 0.12)",
        width: 40,
        height: 25,
    },
    countryListLabel: {
        marginBottom: 8,
        textAlign: "center",
    },
    legendLabel: {
        margin: 4,
        textAlign: "center",
    },
});
